import "./App.css";
import "./style.scss";
import "./media-query.css";
import { Routes, Route } from "react-router-dom";
import React, { lazy, Suspense } from "react";
import { ToastContainer } from "react-toastify";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CommonBackground from "./components/CommonBackground";
import ScrollToTop from "./components/ScrollToTop";
import ExplorePageShimmer from "./components/UI/Shimmers/ExplorePageShimmer";
import MyStoriesPageShimmer from "./components/UI/Shimmers/MyStoriesPageShimmer";
import MyBookmarksPageShimmer from "./components/UI/Shimmers/MyBookmarksPageShimmer";
import StoryDetailsPageShimmer from "./components/UI/Shimmers/StoryDetailsPageShimmer";
import ChapterDetailsPageShimmer from "./components/UI/Shimmers/ChapterDetailsPageShimmer";
import CreateStoryPageShimmer from "./components/UI/Shimmers/CreateStoryPageShimmer";
import DashboardShimmer from "./components/UI/Shimmers/DashboardShimmer";
import CareersPageShimmer from "./components/UI/Shimmers/CareersPageShimmer";
import InkMastersPageShimmer from "./components/UI/Shimmers/InkMastersPageShimmer";
import InkMastersDetailsPageShimmer from "./components/UI/Shimmers/InkMastersDetailsPageShimmer";
import CreateInkMasterPageShimmer from "./components/UI/Shimmers/CreateInkMasterPageShimmer";
import StoryManagementChaptersShimmer from "./components/UI/Shimmers/StoryManagementChaptersShimmer";
import { usePageTracking } from "./hooks/usePageTracking";
import { useAppContext } from "./context/AppContext";
import { AdminProtectedRoute } from "./pages/ProtectedRoute";

// HomePage is not lazy loaded - it's the entry point and should load immediately
import HomePage from "./pages/HomePage";

// Lazy load ToastContainer styles - not critical for initial render
const loadToastStyles = () => import("react-toastify/dist/ReactToastify.css");

// Lazy load routes for code splitting - reduces initial bundle size (except HomePage)
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ExplorePage = lazy(() => import("./pages/ExplorePage"));
const MyStoriesPage = lazy(() => import("./pages/MyStoriesPage"));
const UserStoriesPage = lazy(() => import("./pages/UserStoriesPage"));
const MyBookmarksPage = lazy(() => import("./pages/MyBookmarksPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const UserProfilePageWrapper = lazy(() => import("./pages/UserProfilePageWrapper"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ContentGuidelines = lazy(() => import("./pages/ContentGuidelines"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const CopyrightPolicy = lazy(() => import("./pages/CopyrightPolicy"));
const DataRights = lazy(() => import("./pages/DataRights"));
const AcceptableUsePolicy = lazy(() => import("./pages/AcceptableUsePolicy"));
const EditProfilePage = lazy(() => import("./pages/EditProfilePage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const CreateJobPage = lazy(() => import("./pages/CreateJobPage"));
const CreateStory = lazy(() => import("./pages/CreateStory"));
const StoryDetails = lazy(() => import("./pages/StoryDetails"));
const ChapterDetails = lazy(() => import("./pages/ChapterDetails"));
const StoryManagement = lazy(() => import("./pages/StoryManagement"));
const WhatsNewPage = lazy(() => import("./pages/WhatsNewPage"));
const ModerationQueue = lazy(() => import("./pages/ModerationQueue"));
// Ink Masters routes - lazy loaded
const InkMastersPage = lazy(() => import("./pages/InkMastersPage"));
const InkMastersDetailsPage = lazy(() => import("./pages/InkMastersDetailsPage"));
const CreateInkMasterPage = lazy(() => import("./pages/CreateInkMasterPage"));
const InkMasterFormWrapper = lazy(() => import("./components/InkMasterFormWrapper"));

const App = (): JSX.Element => {
  // Track page views on route changes
  usePageTracking();
  const { user } = useAppContext();

  // Load toast styles asynchronously after initial render
  React.useEffect(() => {
    loadToastStyles();
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        toastClassName="Toastify__toast--glassy"
      />
      <ScrollToTop />
      <Navbar />
      <CommonBackground>
        <Routes>
          {/* HomePage loads immediately - no lazy loading for better FCP */}
          <Route path="/" element={<HomePage />} />
          {/* Lazy loaded routes - only load when navigated to */}
          <Route
            path="/create-story"
            element={
              <Suspense fallback={<CreateStoryPageShimmer />}>
                <CreateStory />
              </Suspense>
            }
          />
          <Route
            path="/story/:id/:title"
            element={
              <Suspense fallback={<StoryDetailsPageShimmer />}>
                <StoryDetails />
              </Suspense>
            }
          />
          <Route
            path="/story/:id"
            element={
              <Suspense fallback={<StoryDetailsPageShimmer />}>
                <StoryDetails />
              </Suspense>
            }
          />
          <Route
            path="/story/:storyId/chapter/:chapterId/:title"
            element={
              <Suspense fallback={<ChapterDetailsPageShimmer />}>
                <ChapterDetails />
              </Suspense>
            }
          />
          <Route
            path="/manage-story/:id"
            element={
              <Suspense fallback={<StoryManagementChaptersShimmer />}>
                <StoryManagement />
              </Suspense>
            }
          />
          {/* Other Routes */}
          <Route
            path="/about"
            element={
              <Suspense>
                <AboutPage />
              </Suspense>
            }
          />
          <Route
            path="/auth"
            element={
              <Suspense>
                <AuthPage />
              </Suspense>
            }
          />
          <Route
            path="/explore"
            element={
              <Suspense fallback={<ExplorePageShimmer />}>
                <ExplorePage />
              </Suspense>
            }
          />
          <Route
            path="/my-stories"
            element={
              <Suspense fallback={<MyStoriesPageShimmer />}>
                <MyStoriesPage />
              </Suspense>
            }
          />
          <Route
            path="/my-bookmarks"
            element={
              <Suspense fallback={<MyBookmarksPageShimmer />}>
                <MyBookmarksPage />
              </Suspense>
            }
          />
          <Route
            path="/contact"
            element={
              <Suspense >
                <ContactPage />
              </Suspense>
            }
          />
          <Route
            path="/user/:userId"
            element={
              <Suspense>
                <UserProfilePageWrapper />
              </Suspense>
            }
          />
          <Route
            path="/user/:userId/stories"
            element={
              <Suspense fallback={<MyStoriesPageShimmer />}>
                <UserStoriesPage />
              </Suspense>
            }
          />
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<DashboardShimmer />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <Suspense>
                <PrivacyPolicy />
              </Suspense>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <Suspense>
                <TermsOfService />
              </Suspense>
            }
          />
          <Route
            path="/content-guidelines"
            element={
              <Suspense>
                <ContentGuidelines />
              </Suspense>
            }
          />
          <Route
            path="/cookie-policy"
            element={
              <Suspense>
                <CookiePolicy />
              </Suspense>
            }
          />
          <Route
            path="/copyright-policy"
            element={
              <Suspense>
                <CopyrightPolicy />
              </Suspense>
            }
          />
          <Route
            path="/data-rights"
            element={
              <Suspense>
                <DataRights />
              </Suspense>
            }
          />
          <Route
            path="/acceptable-use-policy"
            element={
              <Suspense>
                <AcceptableUsePolicy />
              </Suspense>
            }
          />
          <Route
            path="/edit-profile"
            element={
              <Suspense >
                <EditProfilePage />
              </Suspense>
            }
          />
          <Route
            path="/careers/:jobId"
            element={
              <Suspense fallback={<CareersPageShimmer />}>
                <JobDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/careers"
            element={
              <Suspense fallback={<CareersPageShimmer />}>
                <CareersPage />
              </Suspense>
            }
          />
          <Route
            path="/careers/create-job"
            element={
              <Suspense>
                <CreateJobPage />
              </Suspense>
            }
          />
          <Route
            path="/whats-new"
            element={
              <Suspense>
                <WhatsNewPage />
              </Suspense>
            }
          />
          {/* Moderation Queue - Admin Only */}
          <Route
            path="/moderation"
            element={
              <Suspense>
                <AdminProtectedRoute user={user}>
                  <ModerationQueue />
                </AdminProtectedRoute>
              </Suspense>
            }
          />
          {/* Ink Masters Routes */}
          <Route
            path="/challenges/ink-masters"
            element={
              <Suspense fallback={<InkMastersPageShimmer />}>
                <InkMastersPage />
              </Suspense>
            }
          />
          <Route
            path="/challenges/ink-masters/:id"
            element={
              <Suspense fallback={<InkMastersDetailsPageShimmer />}>
                <InkMastersDetailsPage />
              </Suspense>
            }
          />
          <Route
            path="/challenges/ink-masters/create"
            element={
              <Suspense fallback={<CreateInkMasterPageShimmer />}>
                <CreateInkMasterPage />
              </Suspense>
            }
          />
          <Route
            path="/challenges/ink-masters/edit/:id"
            element={
              <Suspense>
                <InkMasterFormWrapper />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense >
                <NotFoundPage />
              </Suspense>
            }
          />
        </Routes>
        <Footer />
      </CommonBackground>
    </>
  );
};

export default App;
